import { RouteLocationNormalized } from 'vue-router';

import { IActions } from '../interfaces/store.interfaces';
import store from '.';
import getters from './getters';
import mutations from './mutations';
import { ApplicationStatusOptions } from '../models/application-status/ApplicationStatusOptions';
import { ParameterType, SortingType } from '@/study-search/enums';
import { ParameterValue } from '@/study-search/domain';
import { EventsSender } from '../infrastructure/events';
import SearchResultsPresenter from '../presenters/SearchResultsPresenter';
import { PaginationDefinition } from '../models/definition';
import { IAutocomplete, IUrlContent } from '../interfaces';
import { router, RoutingDefinitions, RoutingHelper } from '../router';
import { UrlParser } from '../models/url';
import Tracking from '../infrastructure/tracking/Tracking';
import { LocalizedContentPlugin } from './plugins/LocalizedContentPlugin';
import { ParameterService } from '../infrastructure/ParameterService';
import { DataStorage } from '@studyportals/data-storage';
import { FilterKey, IFilterPayload, FilterValuesChangedEvent, ISuggestionPayload } from '@studyportals/search-filters';
import {
	IAppState,
	SearchPageFilterStateChangedEvent,
	SearchPageClearFiltersEvent
} from '@studyportals/search-page-interface';
import { EventAggregationService } from '@/search-page/platform/EventAggregationService';
import { ParsedFilters } from '../models/generalizations/ParsedFilters';
import { DisciplineFaqHandler } from '../models/faq';
import CompatibilityLayer from '../infrastructure/CompatibilityLayer';
import FilterProvider from '../presentation/components/filters/FilterProvider';

const getParameters = async (): Promise<ParameterValue[]> => {
	const parameterService = new ParameterService();
	return parameterService.getParameters();
};

const pageTracking = (): void => {
	const resultsFound = getters.getTotalSearchResults();

	const keywordWhatFilter = store.state.filters.filter((_) => _.type === ParameterType.KEYWORD_WHAT)[0];
	const keywordWhereFilter = store.state.filters.filter((_) => _.type === ParameterType.KEYWORD_WHERE)[0];

	const searchTerms: string[] = [];
	if (keywordWhatFilter && keywordWhatFilter.values.length) {
		searchTerms.push(keywordWhatFilter.values[0]);
	}

	if (keywordWhereFilter && keywordWhereFilter.values.length) {
		searchTerms.push(keywordWhereFilter.values[0]);
	}

	if (searchTerms.length === 0) {
		searchTerms.push('');
	}

	const filtersWithValues = store.state.filters.filter((filter) => filter.values.length > 0);

	const snowPlowData = {};
	filtersWithValues.forEach((_) => (snowPlowData[_.type] = _.values.toString()));

	Tracking.snowPlowSiteSearch(searchTerms, snowPlowData, resultsFound);
};

const navigateFromState = async (): Promise<void> => {
	await RoutingHelper.changeUrl(getters.getFilters(), getters.getQuery(), getters.getPageNumber());
};

const onFilterChange = async (): Promise<void> => {
	mutations.storePageNumber(PaginationDefinition.DEFAULT_PAGE_NUMBER);
	mutations.untrackStudyCardIds();
	await navigateFromState();
	DisciplineFaqHandler.Invalidated = true;
};

const publishFilterStateChanged = (parameters: ParameterValue[]): void => {
	EventAggregationService.instance.publishTo(
		SearchPageFilterStateChangedEvent.EventType,
		new SearchPageFilterStateChangedEvent()
	);
	void publishValuesFiltersChanged(parameters);
};

const publishValuesFiltersChanged = (parameters: ParameterValue[]): void => {
	const searchInput = CompatibilityLayer.mapToProgrammeSearchInput(
		parameters,
		store.getters.getSorting(),
		store.getters.getPortalType()
	);
	EventAggregationService.instance.publishTo(
		FilterValuesChangedEvent.EventType,
		new FilterValuesChangedEvent(searchInput, new FilterProvider())
	);
};

const actions: IActions = {
	setAreDependenciesLoaded: async (areDependenciesLoaded: boolean): Promise<void> => {
		store.mutations.setAreDependenciesLoaded(areDependenciesLoaded);

		if (areDependenciesLoaded) {
			const localizedContentPlugin = new LocalizedContentPlugin();
			localizedContentPlugin.initiate();
		}
	},

	callServices: async (): Promise<void> => {
		if (store.state.applicationStatus !== ApplicationStatusOptions.ERROR) {
			const parameters: ParameterValue[] = await getParameters();

			publishValuesFiltersChanged(parameters);
			await store.actions.updateSearchResults(parameters);

			const metaRefreshInfo = getters.getMetaRefreshInfo();
			let canonicalPath = '';
			if (metaRefreshInfo) {
				canonicalPath = `${location.origin}${metaRefreshInfo}`;
			} else {
				canonicalPath = `${location.origin}${location.pathname}`;
			}
			mutations.storeCanonical(canonicalPath);
			mutations.storeApplicationStatus(ApplicationStatusOptions.READY);

			EventsSender.sendInitialEvents(store.getters.getPortalType());
			publishFilterStateChanged(parameters);
		}
	},

	clearFilterFor: async (key: FilterKey): Promise<void> => {
		mutations.emptyFilterValues(key);

		EventAggregationService.instance.publishTo(
			SearchPageClearFiltersEvent.EventType,
			new SearchPageClearFiltersEvent([key])
		);
		await onFilterChange();
	},

	clearFiltersFor: async (keys: FilterKey[]): Promise<void> => {
		keys.forEach((key) => {
			mutations.emptyFilterValues(key);
		});

		EventAggregationService.instance.publishTo(
			SearchPageClearFiltersEvent.EventType,
			new SearchPageClearFiltersEvent(keys)
		);

		await onFilterChange();
	},

	clearFilterValues: async (payload: IFilterPayload[]): Promise<void> => {
		payload.forEach((_) => mutations.emptyFilterValue(_.key, _.value));

		EventAggregationService.instance.publishTo(
			SearchPageClearFiltersEvent.EventType,
			new SearchPageClearFiltersEvent(payload.map((_) => _.key))
		);
		await onFilterChange();
	},

	clearFilters: async (): Promise<void> => {
		mutations.emptyAllFilterValues();
		mutations.untrackStudyCardIds();

		const keys = getters.getFilters().map((filter) => filter.key);

		if (keys?.length) {
			EventAggregationService.instance.publishTo(
				SearchPageClearFiltersEvent.EventType,
				new SearchPageClearFiltersEvent(keys)
			);
		}

		await navigateFromState();
	},

	applyFilterSuggestion: async (payload: ISuggestionPayload): Promise<void> => {
		payload.toDeselect.forEach((key: FilterKey) => mutations.emptyFilterValues(key));
		payload.toSelect.forEach((value: IFilterPayload) => mutations.processFilterValue(value));
		await onFilterChange();
		window.scrollTo(0, 0);
	},

	processAutocompleteInput: async (payload: IAutocomplete, emptyCurrentFilter?: boolean): Promise<void> => {
		if (emptyCurrentFilter) {
			mutations.emptyAllFilterValues();
		}

		mutations.replaceFilterValues(payload.filters);
		mutations.untrackStudyCardIds();
		window.scrollTo(0, 0);

		await RoutingHelper.changeUrl(getters.getFilters(), getters.getQuery(), PaginationDefinition.DEFAULT_PAGE_NUMBER);
	},

	processFilterSelection: async (payload: IFilterPayload): Promise<void> => {
		if ((getters.getApplicationStatus() as ApplicationStatusOptions) === ApplicationStatusOptions.ERROR) {
			await store.actions.resetApplication();
		}
		mutations.processFilterValue(payload);
		await onFilterChange();
		window.scrollTo(0, 0);
	},

	processFilters: async (payload: IFilterPayload[]): Promise<void> => {
		if ((getters.getApplicationStatus() as ApplicationStatusOptions) === ApplicationStatusOptions.ERROR) {
			await store.actions.resetApplication();
		}

		payload.forEach((_) => mutations.processFilterValue(_));

		await onFilterChange();
		window.scrollTo(0, 0);
	},

	processPageChange: async (pageNumber: number): Promise<void> => {
		mutations.storePreviousPageNumber();
		mutations.storePageNumber(pageNumber);

		await RoutingHelper.changeUrl(getters.getFilters(), getters.getQuery(), pageNumber);
	},

	processUrlContent: async (payload: IUrlContent): Promise<void> => {
		store.mutations.replaceFilterValues(payload.filters);

		const mergedFilters = store.getters.getFilters();
		const parsedSelectedFilters = mergedFilters.filter((_) => _.values.length > 0).map((_) => _.toParsedFilter());

		const areFiltersEqual = ParsedFilters.areEqual(payload.filters, parsedSelectedFilters);

		if (!areFiltersEqual) {
			await navigateFromState();
			return;
		}

		store.mutations.storePageNumber(payload.pageNumber);
		store.mutations.storeSorting(payload.sorting);

		if (RoutingHelper.canBePrettified(mergedFilters, payload.path)) {
			await navigateFromState();
			return;
		}

		await store.actions.callServices();
	},

	processUserCurrencyAndCountryChange: async (userCountry?: string, userCurrency?: string): Promise<void> => {
		let isChanged = false;

		if (userCurrency && userCurrency !== getters.getCurrency()) {
			store.mutations.storeUserCurrency(userCurrency);
			isChanged = true;
		}

		if (userCountry && userCountry !== getters.getUserCountry()) {
			store.mutations.storeUserNationality(userCountry);
			isChanged = true;
		}

		if (isChanged) {
			await store.actions.updateSearchResults(await getParameters());
			EventsSender.sendInitialEvents(store.getters.getPortalType());
			mutations.storeApplicationStatus(ApplicationStatusOptions.READY);
		}
	},

	processSortingClick: async (sorting: SortingType): Promise<void> => {
		if (sorting !== store.getters.getSorting()) {
			store.mutations.storeSorting(sorting);
			await navigateFromState();
		}
	},

	resetApplication: async (): Promise<void> => {
		await router.push({ name: RoutingDefinitions.ROOT });

		store.mutations.storeApplicationStatus(ApplicationStatusOptions.READY);
		store.mutations.untrackStudyCardIds();
	},

	resetApplicationFromErrorPageButton: (): void => {
		store.mutations.storeApplicationStatus(ApplicationStatusOptions.READY);
		store.mutations.untrackStudyCardIds();

		void router.push({ name: RoutingDefinitions.ROOT });
	},

	updateSearchResults: async (parameters: ParameterValue[]): Promise<void> => {
		const page = store.state.page;
		mutations.storeApplicationStatus(ApplicationStatusOptions.FETCHING);

		const { results, facets, count } = await SearchResultsPresenter.retrieveSearchResults(
			parameters,
			store.getters.getPortalType(),
			page.size,
			page.start,
			store.getters.getSorting()
		);

		const previousResults = getters.getSearchResults();
		const isSameQuery =
			JSON.stringify(getters.getPreviousSearchQuery()) === JSON.stringify(parameters) &&
			store.getters.getPreviousSorting() === store.getters.getSorting();
		const isNextPage = getters.getPageNumber() - getters.getPreviousPageNumber() === 1;
		// eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
		const experimentIsControl = DataStorage.retrieve('Experiment/PaginationSearchPageExperiment')?.number === 0;

		if (isSameQuery && isNextPage && experimentIsControl) {
			mutations.storeSearchResults([...previousResults, ...results]);
		} else {
			mutations.storeSearchResults(results);
		}

		if (facets) {
			mutations.storeFacets(facets);
		}

		mutations.storeSearchResultsCount(count);

		mutations.storePreviousSearchQuery(JSON.stringify(Array.from(parameters)));
		store.mutations.storePreviousSorting();
		pageTracking();
	},

	updateSearchResultsFromRoute: async (route: RouteLocationNormalized): Promise<void> => {
		if ((getters.getApplicationStatus() as ApplicationStatusOptions) === ApplicationStatusOptions.INITIALIZING) {
			return;
		}

		const navigationFailed = route.matched.length === 0;
		if (navigationFailed) {
			store.mutations.storeApplicationStatus(ApplicationStatusOptions.ERROR);
		} else {
			await UrlParser.parse(route.params, route.path, route.query, store.state.portalDefinition);
		}
	},

	initializeFrom: (state: IAppState): void => {
		for (const filterKey of Object.keys(state.filterSelection)) {
			const filter = store.state.filters.find((_) => _.key === (filterKey as FilterKey));
			if (filter) {
				for (const value of state.filterSelection[filterKey] ?? []) {
					mutations.processFilterValue({ key: filterKey as FilterKey, value });
				}
			}
		}
	},

	activate: async (state: IAppState): Promise<void> => {
		for (const filterKey of Object.keys(state.filterSelection)) {
			const filter = store.state.filters.find((_) => _.key === (filterKey as FilterKey));
			if (filter) {
				mutations.emptyFilterValues(filterKey as FilterKey);

				for (const value of state.filterSelection[filterKey] ?? []) {
					mutations.processFilterValue({ key: filterKey as FilterKey, value });
				}
			}
		}

		await navigateFromState();
	},

	sleep: (): IAppState => {
		const state = {
			filterSelection: {}
		};

		for (const filter of store.state.filters.filter((f) => f.values.length > 0)) {
			state.filterSelection[filter.key] = [...filter.values];
		}

		return state;
	}
};

export default actions;
